import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';

export interface CacheInfo {
  lastUpdated?: string;
}

export interface CacheAwareRefreshButtonProps {
  onRefresh: () => void;
  isLoading: boolean;
  cacheInfo?: CacheInfo;
}

const formatRelativeTime = (timestamp: string): string => {
  const now = new Date();
  const updated = new Date(timestamp);
  const ageMs = now.getTime() - updated.getTime();

  const minutes = Math.floor(ageMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  const days = Math.floor(hours / 24);
  return `${days}d`;
};

const formatAbsoluteTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const getStaleInfo = (timestamp: string): { color: string; isStale: boolean } => {
  const now = new Date();
  const updated = new Date(timestamp);
  const ageMinutes = (now.getTime() - updated.getTime()) / 60000;

  if (ageMinutes < 60) return { color: 'success.main', isStale: false };
  if (ageMinutes < 300) return { color: 'warning.dark', isStale: false };
  return { color: 'error.main', isStale: true };
};

export const CacheAwareRefreshButton: React.FC<CacheAwareRefreshButtonProps> = ({
  onRefresh,
  isLoading,
  cacheInfo,
}) => {
  const lastUpdated = cacheInfo?.lastUpdated;

  // No cache info - render simple refresh button
  if (!lastUpdated) {
    return (
      <Tooltip title="Refresh data" arrow>
        <IconButton onClick={onRefresh} disabled={isLoading} size="large">
          {isLoading ? <CircularProgress size={24} /> : <RefreshIcon />}
        </IconButton>
      </Tooltip>
    );
  }

  const staleInfo = getStaleInfo(lastUpdated);
  const tooltipTitle = `Last updated: ${formatAbsoluteTime(lastUpdated)}${
    staleInfo.isStale ? '\nConsider refreshing for latest data' : ''
  }`;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle', margin: 0 }}>
      <Tooltip title={tooltipTitle} arrow>
        <IconButton
          onClick={onRefresh}
          disabled={isLoading}
          size="large"
          sx={{
            animation: isLoading ? 'spin 1s linear infinite' : 'none',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Tooltip>
      <Typography
        variant="caption"
        sx={{
          color: staleInfo.color,
          fontSize: '0.75rem',
          fontWeight: 500,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {formatRelativeTime(lastUpdated)}
        {staleInfo.isStale && (
          <Box
            component="span"
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: 'error.main',
            }}
          />
        )}
      </Typography>
    </Box>
  );
};
